import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, get, remove } from "firebase/database";
import * as XLSX from "xlsx";
import app from "../../../firebaseConfig";

import ProteccionAdmin from "./proteccionadmin";
import jsPDF from "jspdf";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";

function AwooRegistro() {
  const [pedidos, setPedidos] = useState([]);
  const [pedidosSeleccionados, setPedidosSeleccionados] = useState([]);
  const seleccionados = pedidos.filter((p) => pedidosSeleccionados.includes(p.id));
  const [datosEmpresa, setDatosEmpresa] = useState({});
  const [numeroFactura, setNumeroFactura] = useState("0001");

  ///// Eliminar Registro
  const [dialogOpen, setDialogOpen] = useState(false);
  const [claveIngresada, setClaveIngresada] = useState("");
  const [pedidoAEliminar, setPedidoAEliminar] = useState(null);
  ////////////////////

  // ---- Helpers para fechas y parsing ----
  const extraerFechaDDMMYYYY = (texto) => {
    if (!texto) return null;
    const regexFecha = /(\d{1,2}\/\d{1,2}\/\d{4})/;
    const m = String(texto).match(regexFecha);
    return m ? m[1] : null;
  };

  const extraerHora = (texto) => {
    if (!texto) return null;
    // busca hh:mm:ss con posible AM/PM o a. m./p. m.
    const regexHora = /(\d{1,2}:\d{2}(?::\d{2})?(\s*[ap]\.?\.?\s*m\.?\.?)?)/i;
    const m = String(texto).match(regexHora);
    return m ? m[1].trim() : null;
  };

  const convertirHoraAM24 = (horaStr) => {
    if (!horaStr) return { horaObj: null, horaFormateada: "" };
    // normalizar "a. m." / "p. m." -> AM/PM
    let s = horaStr.replace(/\s+/g, " ").trim();
    s = s.replace(/a\.?\s*m\.?/i, "AM").replace(/p\.?\s*m\.?/i, "PM");
    // si no trae AM/PM, asumimos 24h
    const regex = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i;
    const m = s.match(regex);
    if (!m) return { horaObj: null, horaFormateada: "" };
    let hh = Number(m[1]);
    const mm = Number(m[2]);
    const ss = Number(m[3] || 0);
    const ampm = m[4];
    if (ampm) {
      if (ampm.toUpperCase() === "PM" && hh !== 12) hh += 12;
      if (ampm.toUpperCase() === "AM" && hh === 12) hh = 0;
    }
    const horaForm = String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0") + ":" + String(ss).padStart(2, "0");
    return { horaObj: { hh, mm, ss }, horaFormateada: horaForm };
  };

  const parseFechaHoraPedido = (pedido) => {
    // Puede venir: pedido.fecha (solo dd/mm/yyyy), pedido.hora separado,
    // o pedido.fecha con fecha+hora (toLocaleString), o formatos antiguos.
    const rawFecha = pedido.fecha || "";
    const rawHora = pedido.hora || "";

    // Intentar extraer fecha y hora desde fecha si trae ambas
    const fechaDesdeFecha = extraerFechaDDMMYYYY(rawFecha);
    const horaDesdeFecha = extraerHora(rawFecha);

    const fechaStr = fechaDesdeFecha || extraerFechaDDMMYYYY(rawHora) || null;
    const horaStr = horaDesdeFecha || rawHora || extraerHora(rawHora) || null;

    let fechaOrdenable = null;

    if (fechaStr) {
      const [d, m, y] = fechaStr.split("/").map(Number);
      if (!isNaN(d) && !isNaN(m) && !isNaN(y)) {
        if (horaStr) {
          const { horaObj } = convertirHoraAM24(horaStr);
          if (horaObj) {
            fechaOrdenable = new Date(y, m - 1, d, horaObj.hh, horaObj.mm, horaObj.ss);
          } else {
            fechaOrdenable = new Date(y, m - 1, d);
          }
        } else {
          fechaOrdenable = new Date(y, m - 1, d);
        }
      }
    } else {
      // intentar crear Date con todo el string (por si viene en formato ISO o toLocale)
      const d = new Date(String(rawFecha || rawHora));
      if (!isNaN(d)) fechaOrdenable = d;
    }

    return {
      fechaStr: fechaStr || "",
      horaStr: horaStr || "",
      fechaOrdenable: fechaOrdenable || new Date(0),
    };
  };

  // ---- Actualiza número de factura basado en OrdenesPagadas (si se selecciona 1 pedido) ----
  useEffect(() => {
    const obtenerNumeroFactura = async () => {
      if (seleccionados.length === 0) {
        setNumeroFactura("0001");
        return;
      }

      try {
        const db = getDatabase(app);
        const pagRef = ref(db, "nature/Awoo/OrdenesPagadas");
        const snap = await get(pagRef);
        if (!snap.exists()) {
          setNumeroFactura("0001");
          return;
        }
        const keys = Object.keys(snap.val());
        const idActual = seleccionados[0]?.id;
        const index = keys.indexOf(idActual);
        if (index !== -1) {
          const num = (index + 1).toString().padStart(4, "0");
          setNumeroFactura(num);
        } else {
          // si no lo encuentra, asignar siguiente
          setNumeroFactura((keys.length + 1).toString().padStart(4, "0"));
        }
      } catch (error) {
        console.error("Error obteniendo número de factura:", error);
      }
    };

    obtenerNumeroFactura();
  }, [seleccionados]);

  // ---- Cargar últimos pedidos desde OrdenesPagadas (adaptable a ambos formatos) ----
  useEffect(() => {
    const db = getDatabase(app);
    const pedidosRef = ref(db, "nature/Awoo/OrdenesPagadas");

    onValue(pedidosRef, (snapshot) => {
      const data = snapshot.val() || {};
      const arr = Object.entries(data)
        .map(([key, value]) => {
          // parse fecha/hora robusto
          const { fechaStr, horaStr, fechaOrdenable } = parseFechaHoraPedido(value);

          // construir productos normalizados
          let productosNorm = [];

          // Caso antiguo: value.productos (obj)
          if (value.productos && typeof value.productos === "object") {
            productosNorm = Object.values(value.productos).map((p) => ({
              titulo: p.titulo || p.producto || "Sin título",
              cantidad: Number(p.cantidad) || 0,
              precioUnitario: Number(p.precioUnitario || p.precio || p.precioUnd || 0),
              costoUnitario: Number(p.costoUnitario || p.costo || p.costoUnd || 0),
              total: Number(p.total || (Number(p.precioUnitario || p.precio || 0) * Number(p.cantidad || 0))) || 0,
            }));
          } else {
            // Caso nuevo: pedido único con campos en raíz
            // Puede venir como producto string o como objeto
            if (value.producto) {
              if (typeof value.producto === "object") {
                const titulo = value.producto.titulo || value.producto.nombre || "Sin título";
                const cantidad = Number(value.cantidad || value.producto.cantidad || 0);
                const precioUnitario = Number(value.precioUnd || value.producto.precioUnitario || value.producto.precio || 0);
                const costoUnitario = Number(value.costoUnd || value.producto.costoUnitario || 0);
                const total = Number(value.precioTotal || precioUnitario * cantidad || 0);
                productosNorm.push({ titulo, cantidad, precioUnitario, costoUnitario, total });
              } else {
                const titulo = String(value.producto);
                const cantidad = Number(value.cantidad || 1);
                const precioUnitario = Number(value.precioUnd || value.precioUnitario || 0);
                const costoUnitario = Number(value.costoUnd || value.costoUnitario || 0);
                const total = Number(value.precioTotal || precioUnitario * cantidad || 0);
                productosNorm.push({ titulo, cantidad, precioUnitario, costoUnitario, total });
              }
            } else if (value.titulo) {
              // otro posible formato en la raíz
              const titulo = value.titulo;
              const cantidad = Number(value.cantidad || 1);
              const precioUnitario = Number(value.precioUnitario || value.precioUnd || 0);
              const costoUnitario = Number(value.costoUnitario || value.costoUnd || 0);
              const total = Number(value.precioTotal || precioUnitario * cantidad || 0);
              productosNorm.push({ titulo, cantidad, precioUnitario, costoUnitario, total });
            }
          }

          // Si no hay productos detectados, intentar crear uno genérico con precioTotal
          if (productosNorm.length === 0) {
            const cantidad = Number(value.cantidad || 1);
            const total = Number(value.precioTotal || value.totalPedido || 0);
            const precioUnitario = cantidad ? total / cantidad : 0;
            productosNorm.push({
              titulo: String(value.producto || value.titular || "Venta"),
              cantidad,
              precioUnitario,
              costoUnitario: Number(value.costoTotal || 0) / (cantidad || 1),
              total,
            });
          }

          return {
            id: key,
            ...value,
            fechaStr,
            horaStr,
            fechaOrdenable,
            productos: productosNorm,
          };
        })
        .sort((a, b) => b.fechaOrdenable - a.fechaOrdenable);

      setPedidos(arr.slice(0, 50)); // ajustar la cantidad a mostrar si quieres
    });
  }, []);

  // ---- Cargar datos empresa para la factura ----
  useEffect(() => {
    const db = getDatabase(app);
    const refDatos = ref(db, "nature/Awoo/Factura/Datos");
    onValue(refDatos, (snapshot) => {
      if (snapshot.exists()) {
        setDatosEmpresa(snapshot.val());
      }
    });
  }, []);

  // ---- Eliminar pedido (desde OrdenesPagadas) ----
  const handleEliminarPedido = async () => {
    if (!pedidoAEliminar) return;
    const db = getDatabase(app);
    const claveRef = ref(db, "nature/Awoo/Control/Seguridad/Admin");

    try {
      const snapshot = await get(claveRef);
      if (snapshot.exists()) {
        const claveCorrecta = snapshot.val();
        if (claveIngresada === claveCorrecta) {
          await remove(ref(db, `nature/Awoo/OrdenesPagadas/${pedidoAEliminar}`));
          setDialogOpen(false);
          setClaveIngresada("");
          setPedidoAEliminar(null);
          alert("Pedido eliminado exitosamente.");
        } else {
          alert("Clave incorrecta.");
        }
      } else {
        alert("No se encontró la clave de administrador en la BD.");
      }
    } catch (error) {
      console.error("Error eliminando pedido:", error);
      alert("Error eliminando pedido.");
    }
  };

  // ---- Descargar factura (solo si hay 1 pedido seleccionado) ----
  const descargarFacturaMultiple = () => {
    if (seleccionados.length === 0) return;

    // si es más de uno, agrupar productos (se mantiene nombre del primer pedido para mesa/empleado)
    const pdf = new jsPDF({ unit: "mm", format: [58, 200] });
    let y = 10;
    const pageWidth = pdf.internal.pageSize.getWidth();

    const centerText = (text, yPos, size = 10) => {
      pdf.setFontSize(size);
      const width = pdf.getTextWidth(text);
      pdf.text(String(text), (pageWidth - width) / 2, yPos);
    };

    pdf.setFont("helvetica", "bold");
    centerText(datosEmpresa.Empresa || "", y, 12);
    y += 5;
    pdf.setFont("helvetica", "normal");
    centerText(datosEmpresa.Descripción || "", y, 8);
    y += 4;
    centerText(datosEmpresa.Dirección || "", y, 8);
    y += 4;
    centerText(datosEmpresa.Celular || "", y, 8);
    y += 4;
    pdf.line(5, y, 53, y);
    y += 4;

    pdf.setFont("helvetica", "bold");
    centerText("Factura Venta", y, 8);
    y += 4;
    pdf.setFont("helvetica", "normal");
    centerText(`${numeroFactura}`, y, 8);
    y += 2;
    pdf.line(5, y, 53, y);
    y += 4;

    const ahora = new Date();
    const fechaHora = ahora.toLocaleString("es-CO");

    pdf.setFont("helvetica", "bold");
    pdf.text("Fecha:", 5, y);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(fechaHora, 18, y);
    y += 6;
    pdf.line(5, y, 53, y);
    y += 4;

    const mesa = seleccionados[0]?.mesa || "Sin número";
    const titulares = [...new Set(seleccionados.map((p) => p.titular).filter(Boolean))];

    pdf.setFont("helvetica", "bold");
    pdf.text("Mesa:", 5, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(mesa), 18, y);
    y += 4;

    pdf.setFont("helvetica", "bold");
    pdf.text("Cliente:", 5, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(titulares.join(" / "), 18, y);
    y += 4;

    pdf.setFont("helvetica", "bold");
    pdf.text("Mesero:", 5, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(String(seleccionados[0]?.empleado || ""), 18, y);
    y += 6;

    pdf.line(5, y, 53, y);
    y += 4;

    // Agrupar productos
    const productosMap = {};

    seleccionados.forEach((p) => {
      (p.productos || []).forEach((prod) => {
        const key = prod.titulo;
        if (!productosMap[key]) {
          productosMap[key] = { ...prod };
        } else {
          productosMap[key].cantidad = (productosMap[key].cantidad || 0) + (prod.cantidad || 0);
          productosMap[key].total = (productosMap[key].total || 0) + (prod.total || (prod.precioUnitario || 0) * (prod.cantidad || 0));
        }
      });
    });

    pdf.setFont("helvetica", "bold");
    pdf.text("Producto", 5, y);
    pdf.text("Precio", 40, y);
    y += 4;
    pdf.line(5, y, 53, y);
    y += 4;

    pdf.setFont("helvetica", "normal");
    Object.values(productosMap).forEach((prod) => {
      pdf.text(`${prod.titulo} x${prod.cantidad}`, 5, y);
      pdf.text(`$${Number(prod.total || 0).toLocaleString("es-CO")}`, 40, y);
      y += 6;
      // si necesitamos paginar, podríamos agregar lógica aquí
    });

    y += 4;
    pdf.line(5, y, 53, y);
    y += 4;

    const totalFactura = Object.values(productosMap).reduce((sum, p) => sum + (Number(p.total || 0)), 0);

    pdf.setFont("helvetica", "bold");
    pdf.text("Total:", 5, y);
    pdf.text(`$${Number(totalFactura).toLocaleString("es-CO")}`, 40, y);
    y += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    const textoResolucion =
      "FACTURACIÓN ELECTRÓNICA SEGÚN RESOLUCIÓN DIAN No.18763003159636.\n" +
      "La presente factura se asimila en todos sus\n" +
      "efectos legales a la letra de cambio (art. 774 del C.C).";

    pdf.text(textoResolucion, 5, y, { maxWidth: 48, align: "left" });

    pdf.save(`Factura_Mesa_${mesa}.pdf`);
  };

  // ---- Exportar a Excel (OrdenesPagadas) ----
  const exportToExcel = async () => {
    const db = getDatabase(app);
    const pedidosRef = ref(db, "nature/Awoo/OrdenesPagadas");

    try {
      const snapshot = await get(pedidosRef);
      if (!snapshot.exists()) {
        alert("No hay pedidos pagados para exportar.");
        return;
      }
      const pedidosData = snapshot.val();
      const pedidosArray = Object.entries(pedidosData).map(([key, value]) => {
        const { fechaStr, horaStr, fechaOrdenable } = parseFechaHoraPedido(value);
        // normalizar productos
        let productosArray = [];

        if (value.productos && typeof value.productos === "object") {
          productosArray = Object.values(value.productos).map((p) => ({
            titulo: p.titulo || p.producto || "Sin título",
            cantidad: Number(p.cantidad) || 0,
            precioUnitario: Number(p.precioUnitario || p.precio || p.precioUnd || 0),
            costoUnitario: Number(p.costoUnitario || p.costo || p.costoUnd || 0),
            total: Number(p.total || (Number(p.precioUnitario || p.precio || 0) * Number(p.cantidad || 0))) || 0,
          }));
        } else if (value.producto) {
          if (typeof value.producto === "object") {
            const cantidad = Number(value.cantidad || value.producto.cantidad || 0);
            productosArray.push({
              titulo: value.producto.titulo || value.producto.nombre || "Sin título",
              cantidad,
              precioUnitario: Number(value.precioUnd || value.producto.precioUnitario || 0),
              costoUnitario: Number(value.costoUnd || value.producto.costoUnitario || 0),
              total: Number(value.precioTotal || value.producto.total || 0),
            });
          } else {
            const cantidad = Number(value.cantidad || 1);
            productosArray.push({
              titulo: String(value.producto),
              cantidad,
              precioUnitario: Number(value.precioUnd || value.precioUnitario || 0),
              costoUnitario: Number(value.costoUnd || value.costoUnitario || 0),
              total: Number(value.precioTotal || 0),
            });
          }
        } else {
          // fallback
          const cantidad = Number(value.cantidad || 1);
          productosArray.push({
            titulo: value.titular || "Venta",
            cantidad,
            precioUnitario: Number(value.precioTotal || 0) / (cantidad || 1),
            costoUnitario: Number(value.costoTotal || 0) / (cantidad || 1),
            total: Number(value.precioTotal || 0),
          });
        }

        return {
          id: key,
          titular: value.titular || "",
          fecha: fechaStr || value.fecha || "",
          hora: horaStr || value.hora || "",
          mesa: value.mesa || "",
          empleado: value.empleado || "",
          productos: productosArray,
          totalPedido: Number(value.precioTotal || value.totalPedido || productosArray.reduce((s, p) => s + (p.total || p.precioUnitario * p.cantidad), 0)),
          costoTotal: Number(value.costoTotal || productosArray.reduce((s, p) => s + ((p.costoUnitario || 0) * (p.cantidad || 0)), 0)),
          gananciaTotal: Number(value.gananciaTotal || 0),
          fechaOrdenable,
        };
      });

      // ordenar
      const pedidosOrdenados = pedidosArray.sort((a, b) => b.fechaOrdenable - a.fechaOrdenable);

      // preparar worksheet
      const worksheetData = [];

      pedidosOrdenados.forEach((pedido, index) => {
        const { titular, fecha, hora, mesa, productos, empleado, totalPedido, costoTotal, gananciaTotal } = pedido;

        // fila resumen
        worksheetData.push({
          "Pedido #": index + 1,
          "Fecha": fecha,
          "Hora": hora,
          "Mesa": mesa,
          "Titular": titular,
          "Empleado": empleado,
          "Producto": "",
          "Cantidad": "",
          "Precio Unitario": "",
          "Costo Unitario": "",
          "Recaudo Previo": totalPedido,
          "Costo Total": costoTotal,
          "Ganancia": gananciaTotal,
        });

        // filas productos
        productos.forEach((prod) => {
          worksheetData.push({
            "Pedido #": "",
            "Fecha": "",
            "Hora": "",
            "Mesa": "",
            "Titular": "",
            "Empleado": "",
            "Producto": prod.titulo,
            "Cantidad": prod.cantidad,
            "Precio Unitario": prod.precioUnitario,
            "Costo Unitario": prod.costoUnitario,
            "Recaudo Previo": "",
            "Costo Total": "",
            "Ganancia": "",
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "OrdenesPagadas");
      XLSX.writeFile(workbook, "Registro_OrdenesPagadas.xlsx");
    } catch (error) {
      console.error("Error exportando a Excel:", error);
      alert("Ocurrió un error al exportar los pedidos.");
    }
  };

  // ---- Render ----
  return (
    <ProteccionAdmin>
      <div style={{ padding: "20px", backgroundColor: "#111", color: "#fff", minHeight: "100vh" }}>
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>📋 Registro de Pedidos</h2>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={exportToExcel}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
            }}
          >
            📥 Descargar Excel
          </button>

          {pedidosSeleccionados.length >= 1 && (
            <button
              onClick={descargarFacturaMultiple}
              style={{
                padding: "10px 20px",
                backgroundColor: "#2196F3",
                color: "#fff",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "10px",
                border: "none",
                cursor: "pointer",
              }}
            >
              📄 Descargar Factura
            </button>
          )}

          {pedidosSeleccionados.length === 1 && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setPedidoAEliminar(pedidosSeleccionados[0]);
                setDialogOpen(true);
              }}
              style={{
                fontSize: "18px",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "2px solid #f44336",
                color: "#f44336",
                cursor: "pointer",
                minWidth: "50px",
              }}
            >
              🗑️
            </Button>
          )}
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
          <thead>
            <tr>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}></th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Pedido #</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Titular</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Fecha</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Hora</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Mesa</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Mesero</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Producto</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Cantidad</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Precio Unitario</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Costo Unitario</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Recaudo Previo</th>
              <th style={{ borderBottom: "2px solid white", padding: "10px" }}>Ganancia</th>
            </tr>
          </thead>
          <tbody>
            {pedidos.map((pedido, index) => {
              const productos = pedido.productos || [];
              const recaudoPrevio = Number(pedido.totalPedido || pedido.precioTotal || productos.reduce((s, p) => s + (Number(p.total || p.precioUnitario * p.cantidad || 0)), 0));
              const costoTotal = Number(pedido.costoTotal || productos.reduce((s, p) => s + ((Number(p.costoUnitario || 0) * Number(p.cantidad || 0))), 0));
              const ganancia = Number(pedido.gananciaTotal || (recaudoPrevio - costoTotal));

              return productos.map((producto, idx) => {
                return (
                  <tr
                    key={`${pedido.id}-${idx}`}
                    style={{
                      borderBottom: idx === productos.length - 1 ? "3px solid white" : "1px solid gray",
                    }}
                  >
                    <td style={{ padding: "10px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={pedidosSeleccionados.includes(pedido.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPedidosSeleccionados([...pedidosSeleccionados, pedido.id]);
                          } else {
                            setPedidosSeleccionados(pedidosSeleccionados.filter((id) => id !== pedido.id));
                          }
                        }}
                      />
                    </td>

                    <td style={{ padding: "10px" }}>{idx === 0 ? index + 1 : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? pedido.titular : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? (pedido.fechaStr || pedido.fecha) : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? (pedido.horaStr || pedido.hora) : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? pedido.mesa : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? pedido.empleado : ""}</td>

                    <td style={{ padding: "10px" }}>{producto.titulo}</td>
                    <td style={{ padding: "10px" }}>{producto.cantidad}</td>
                    <td style={{ padding: "10px" }}>{Number(producto.precioUnitario || 0).toLocaleString("es-CO", { minimumFractionDigits: 0 })}</td>
                    <td style={{ padding: "10px" }}>{Number(producto.costoUnitario || 0).toLocaleString("es-CO", { minimumFractionDigits: 0 })}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? Number(recaudoPrevio).toLocaleString("es-CO", { minimumFractionDigits: 0 }) : ""}</td>
                    <td style={{ padding: "10px" }}>{idx === 0 ? Number(ganancia).toLocaleString("es-CO", { minimumFractionDigits: 0 }) : ""}</td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Eliminar Registro</DialogTitle>
        <DialogContent>
          <p>Para eliminar este registro, ingresa la clave de administrador:</p>
          <TextField
            autoFocus
            margin="dense"
            label="Clave de Admin"
            type="password"
            fullWidth
            variant="outlined"
            value={claveIngresada}
            onChange={(e) => setClaveIngresada(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleEliminarPedido}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </ProteccionAdmin>
  );
}

export default AwooRegistro;
